import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CardArt from './CardArt';
import colors from '../theme/colors';
import { IconWarning, IconStethoscope, IconHourglass, IconBooks, IconSparkle, iconMarkup } from './icons';

// Slightly lighter shade of themePrimary — matches CARD_FRAME_COLOR used by the tree cards
const themePrimaryHsl = colors.utils.rgbToHsl(colors.base.themePrimary);
const CARD_FRAME_COLOR = colors.utils.hslToRgb(themePrimaryHsl.h, themePrimaryHsl.s, themePrimaryHsl.l + 5);

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

/* White-bordered card frame. No blue mat any more: the panel inside carries the
   blue itself, so the border sits straight on the content like a playing card. */
const PanelFrame = styled(motion.div)`
  background: ${colors.base.themePrimaryTransparent};
  border-radius: 20px;
  padding: 0;
  max-width: 600px;
  width: 100%;
  height: 80vh;

  @media (min-width: 769px) {
    height: 90vh;
    max-width: 900px;
  }

  border: 4px solid ${colors.base.themeSecondary};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
`;

/* Same treatment as the header, the other modals and the card back: theme blue
   with white streaks. The Sources / Who-can-help / Summary boxes stay off-white
   on top of it, so the reading surfaces are still light. */
const DetailsPanel = styled.div`
  background-color: ${colors.base.themePrimaryTransparent};
  background-image:
    linear-gradient(73deg,  transparent calc(47% - 1px), rgba(255,255,255,0.14) 47%, transparent calc(47% + 1px)),
    linear-gradient(161deg, transparent calc(38% - 1px), rgba(255,255,255,0.10) 38%, transparent calc(38% + 1px)),
    linear-gradient(210deg, transparent calc(55% - 1px), rgba(255,255,255,0.08) 55%, transparent calc(55% + 1px)),
    linear-gradient(30deg,  transparent calc(68% - 1px), rgba(255,255,255,0.06) 68%, transparent calc(68% + 1px));
  /* Concentric with PanelFrame: outer radius minus its 4px border */
  border-radius: 16px;
  padding: 30px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  font-size: 11px;
`;

/* Fills remaining height below the fixed header; scrolls internally instead of the whole panel */
const DetailsScrollBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* The summary carries its own scrollbar (see SectionContent $scroll), so this stays
     unscrolled in the normal case. overflow-y is the fallback for the rare node whose
     sources alone are taller than the panel — nothing should ever be unreachable. */
  overflow-y: auto;
  /* Keep the text off the scrollbar: the panel padding sits OUTSIDE this
     scroll box, so without this the content would touch the scrollbar on the right */
  padding-right: 16px;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 4px;
    margin: 15px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.45);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.7);
    }
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.45) rgba(255, 255, 255, 0.08);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${colors.utils.withAlpha(colors.base.themeTertiary, 0.75)};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.14);
    color: ${colors.base.themeTertiary};
  }
`;

const NodeHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 25px;
  padding-right: 50px;
`;

const NodeIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 15px;
  border: 1px solid ${colors.base.themeSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  overflow: hidden;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const NodeInfo = styled.div`
  flex: 1;
`;

const NodeTitle = styled.h2`
  margin: 0 0 10px 0;
  color: ${colors.base.themeTertiary};
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
`;

/* Breadcrumb to the node's ancestors — orientation only, so it is deliberately
   small. Was a sky-blue #4A90E2 pill, a colour found nowhere else in the site;
   it now borrows themePrimary to sit in the same navy family as the cards.
   Sized so a 3-segment chain fits on one line; the tree's longest (4 segments,
   85 chars) still wraps, which is why flex-wrap stays. */
const NodePath = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  max-width: 100%;
  padding: 3px 9px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: ${colors.utils.withAlpha(colors.base.themeTertiary, 0.8)};
  font-size: 0.68rem;
  font-weight: 500;
  line-height: 1.35;
  overflow-wrap: break-word;
`;

/* Chevron between segments — replaces the raw ">" the path used to be joined
   with, so the hierarchy reads without competing with the labels */
const PathSeparator = styled.span`
  color: ${colors.utils.withAlpha(colors.base.themeTertiary, 0.45)};
  font-weight: 400;
  user-select: none;
`;

/* Wraps the breadcrumb and the trial-duration badge so both sit on the same
   row under the title when there's room, and wrap cleanly when there isn't */
const HeaderMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

/* Trial duration shown as a plain recognizable fact next to the title —
   no dedicated section, just a number the reader can spot at a glance */
const TrialBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: ${colors.base.themeTertiary};
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
`;

const Section = styled.div`
  margin-bottom: 25px;

  /* The summary takes the height left over and scrolls inside itself — but only where
     there is room for it. On a phone the panel is too short to split into two scroll
     areas: squeezing it there left a 25px sliver, so below the breakpoint the summary
     flows at its natural height and the panel scrolls as it always did. */
  ${props => props.$fill && `
    @media (min-width: 769px) {
      flex: 1 1 0;
      min-height: 220px;
      margin-bottom: 0;
      display: flex;
      flex-direction: column;
    }
  `}
`;

/* Sources and "who can help" keep their natural height above the summary */
const StaticSections = styled.div`
  flex: 0 0 auto;
`;

/* "AI-generated" disclaimer — a quiet neutral pill. It was 0.4rem orange text,
   which shouted for attention while being too small to actually read; the grey
   recedes but the border and padding keep it readable at a glance. */
const AiBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.035);
  border: 1px solid rgba(0, 0, 0, 0.09);
  color: #8a9099;
  font-size: 0.62rem;
  font-weight: 500;
  line-height: 1.5;
  /* The label spells out where the summary comes from, so it is too long to keep on one
     line on a phone — it used to be a two-word pill. Let it wrap instead of overflowing. */
  max-width: 100%;
  text-align: left;
`;

const SectionContent = styled.div`
  color: #555;
  line-height: 1.5;
  font-size: 0.8rem;
  text-align: justify;
  hyphens: auto;
  overflow-wrap: break-word;

  /* The summary is always open now, so it is the block that scrolls — not the modal.
     min-height keeps it from being squeezed into an unusable sliver: if the panel can't
     honour it, the panel scrolls instead (see DetailsScrollBody). */
  ${props => props.$scroll && `
    @media (min-width: 769px) {
      flex: 1 1 0;
      min-height: 120px;
      overflow-y: auto;
      padding-right: 12px;

      &::-webkit-scrollbar { width: 5px; }
      &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.05);
        border-radius: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background: ${CARD_FRAME_COLOR};
        border-radius: 4px;
      }
      scrollbar-width: thin;
      scrollbar-color: ${CARD_FRAME_COLOR} rgba(0, 0, 0, 0.05);
    }
  `}

  p {
    margin: 0 0 12px;
  }

  strong {
    font-weight: bold;
  }

  strong.desc-title {
    display: block;
    margin-top: 20px;
    margin-bottom: 10px;
  }

  /* ⚠️ These two resets must stay scoped to the FIRST/LAST block, never written as a
     bare p:last-child / strong.desc-title:first-child.
     The AI summary streams each block into its own FadeInBlock wrapper, so every
     paragraph is the last child of its wrapper and every header is the first child
     of its wrapper — a bare selector matched ALL of them and flattened the spacing,
     leaving each bold header glued under the paragraph above it.
     The second selector in each pair covers the non-streamed case, where the whole
     description sits directly in this container. */
  > *:last-child > p:last-child,
  > p:last-child {
    margin-bottom: 0;
  }

  > *:first-child > strong.desc-title:first-child,
  > strong.desc-title:first-child {
    margin-top: 0;
  }

  em {
    font-style: italic;
  }

  a {
    color: #4A90E2;
    text-decoration: none;
    border-bottom: 1px solid #4A90E2;
    transition: all 0.2s;

    &:hover {
      color: #357ABD;
      border-bottom-color: #357ABD;
    }
  }

  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }

  li {
    margin: 4px 0;
  }
`;

const SpecialistInfo = styled.div`
  background: ${colors.base.themeTertiary};
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${CARD_FRAME_COLOR};
  padding: 20px;
  border-radius: 10px;
  margin-top: 15px;

  ${props => props.$fill && `
    @media (min-width: 769px) {
      flex: 1 1 0;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  `}
`;

const SpecialistTitle = styled.h4`
  color: ${CARD_FRAME_COLOR};
  margin: 0 0 15px 0;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SourcesList = styled.div`
  width: 100%;
  color: #495057;

  a {
    color: #4A90E2;
    text-decoration: none;
    border-bottom: 1px solid #4A90E2;
    transition: all 0.2s;

    &:hover {
      color: #357ABD;
      border-bottom-color: #357ABD;
    }
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    column-count: 2;
    column-gap: 20px;
    width: 100%;

    li {
      margin: 0 0 8px 0;
      line-height: 1.4;
      position: relative;
      padding-left: 20px;
      break-inside: avoid;

      &:before {
        content: "•";
        position: absolute;
        left: 0;
        color: ${CARD_FRAME_COLOR};
        font-weight: bold;
      }
    }
  }
`;

/* "Who can help" now shows names only — chips read faster than a bulleted
   list and don't imply there's more detail to read per specialist */
const SpecialistChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SpecialistChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 20px;
  background: ${colors.utils.withAlpha(CARD_FRAME_COLOR, 0.1)};
  border: 1px solid ${colors.utils.withAlpha(CARD_FRAME_COLOR, 0.25)};
  color: #495057;
  font-size: 0.75rem;
  font-weight: 500;
`;

/* Title of the always-open summary. Wraps because the AI badge sits inline with it. */
const SummaryTitle = styled(SpecialistTitle)`
  flex-wrap: wrap;
  flex-shrink: 0;
`;

/* Each revealed block of the AI summary fades/slides in on mount only — once
   rendered it keeps its key and never re-triggers, which is what makes the
   reveal read as a stream instead of the whole text re-animating each tick */
const FadeInBlock = styled.div`
  animation: nodeDetailsFadeIn 0.35s ease;

  @keyframes nodeDetailsFadeIn {
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const TypingCursor = styled.span`
  display: inline-block;
  width: 2px;
  height: 0.9em;
  margin-left: 2px;
  background: ${CARD_FRAME_COLOR};
  vertical-align: text-bottom;
  animation: nodeDetailsBlink 0.9s steps(1) infinite;

  @keyframes nodeDetailsBlink {
    50% { opacity: 0; }
  }
`;

// Break the AI description into individually revealable HTML blocks
// (a paragraph, a heading, or a whole list), instead of one markdown string —
// the AI-summary container reveals these one at a time to fake a live typing feel.
const buildDescriptionBlocks = (text) => {
  if (!text) return [];

  // Normalize line endings (CRLF to LF) and trim the outer whitespace
  const src = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();

  // Inline formatting shared by every block: links, bold, italic
  const inline = (s) => s
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>');

  // A run of text lines becomes one <p>; a line that is only **...** becomes a
  // block heading (.desc-title). A single newline inside a paragraph is a <br>.
  const renderTextLines = (lines) => {
    const out = [];
    let para = [];
    const flushPara = () => {
      if (para.length) { out.push(`<p>${para.map(inline).join('<br>')}</p>`); para = []; }
    };
    lines.forEach((line) => {
      const heading = line.trim().match(/^\*\*(.+?)\*\*$/);
      if (heading) {
        flushPara();
        out.push(`<strong class="desc-title">${inline(heading[1])}</strong>`);
      } else {
        para.push(line);
      }
    });
    flushPara();
    return out;
  };

  // Split on blank lines into blocks; each block is a list or a run of paragraphs/
  // headings. Flattened into one array so each heading/paragraph/list can stream in
  // as its own step.
  const blocks = src.split(/\n{2,}/).flatMap((block) => {
    if (!block.trim()) return [];
    const lines = block.replace(/^\n+|\n+$/g, '').split('\n');
    if (lines.every((l) => /^- /.test(l.trim()))) {
      return [`<ul><li>${lines.map((l) => inline(l.trim().replace(/^- /, ''))).join('</li><li>')}</li></ul>`];
    }
    if (lines.every((l) => /^\d+\. /.test(l.trim()))) {
      return [`<ol><li>${lines.map((l) => inline(l.trim().replace(/^\d+\. /, ''))).join('</li><li>')}</li></ol>`];
    }
    return renderTextLines(lines);
  });

  // Node descriptions carry a ⚠️ emoji (718 occurrences across the tree);
  // swap it for the site's stroke icon so it matches the buttons and modals.
  // Done last so the injected markup can't interfere with the parsing above.
  return blocks.map((block) => block.replace(/⚠️?/g, iconMarkup('warning', 14)));
};

// "Who can help" now shows specialist names only, no explanation text —
// e.g. "Neurologist<br/>  → For spasticity" becomes just "Neurologist"
const extractSpecialistNames = (text) => {
  if (!text) return [];
  return text.split(';')
    .map((item) => item.split(/<br\s*\/?>|→/i)[0].trim())
    .filter(Boolean);
};

// Render links from format: (title,url); (title2,url2)
const renderSourceLinks = (text) => {
  if (!text) return '';

  // Match pattern (title,url) - the url may itself contain parentheses, so the closing one
  // is the last before the next entry (;), the next opening parenthesis or the end of the string
  const linkPattern = /\(([^,]+),(.+?)\)(?=\s*(?:;|\(|$))/g;
  const matches = [...text.matchAll(linkPattern)];

  if (matches.length === 0) return '';

  // Create list HTML with links
  const listItems = matches.map(match => {
    const title = match[1].trim();
    const url = match[2].trim();
    return `<li><a href="${url}" target="_blank" rel="noopener noreferrer">${title}</a></li>`;
  }).join('');

  return `<ul>${listItems}</ul>`;
};

const NodeDetails = ({ node, onClose, allNodes = [] }) => {
  const { t } = useTranslation(); // Added for i18n

  const [visibleCount, setVisibleCount] = React.useState(0);
  const descriptionBlocks = React.useMemo(
    () => buildDescriptionBlocks(node?.description),
    [node?.description]
  );

  // Reveals one more description block at a random 250-600ms interval, so the
  // pre-generated text reads as if it's arriving live. Restarts on every node.
  React.useEffect(() => {
    if (descriptionBlocks.length === 0) {
      setVisibleCount(0);
      return undefined;
    }

    let cancelled = false;
    let i = 0;
    let timeoutId;

    const revealNext = () => {
      if (cancelled) return;
      i += 1;
      setVisibleCount(i);
      if (i < descriptionBlocks.length) {
        timeoutId = setTimeout(revealNext, 250 + Math.random() * 350);
      }
    };

    timeoutId = setTimeout(revealNext, 150);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [node?.id, descriptionBlocks]);

  // Handle Escape key to close modal
  React.useEffect(() => {
    if (!node) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [node, onClose]);

  if (!node) return null;

  // Build parent chain (excluding level 0 root node)
  const getParentChain = (currentNode) => {
    const chain = [];
    let currentParentId = currentNode.parentId;

    while (currentParentId) {
      // eslint-disable-next-line no-loop-func
      const parent = allNodes.find(n => n.id === currentParentId);
      if (parent) {
        // Only add to chain if parent is not level 0 (root node)
        if (parent.level !== 0) {
          chain.unshift(parent.title);
        }
        currentParentId = parent.parentId;
      } else {
        break;
      }
    }

    return chain;
  };

  const parentChain = getParentChain(node);

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const panelVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    }
  };

  return (
    <Overlay
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={onClose}
    >
      <PanelFrame
        variants={panelVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <DetailsPanel>
          <CloseButton onClick={onClose}>×</CloseButton>

          <NodeHeader>
            <NodeIcon>
              <CardArt id={node.id} title={node.title} />
            </NodeIcon>
            <NodeInfo>
              <NodeTitle>{node.title}</NodeTitle>
              <HeaderMeta>
                {parentChain.length > 0 && (
                  <NodePath>
                    {parentChain.map((segment, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <PathSeparator>›</PathSeparator>}
                        <span>{segment}</span>
                      </React.Fragment>
                    ))}
                  </NodePath>
                )}
                {node.trialDuration && node.trialDuration.trim() && (
                  <TrialBadge title={t('tree.trialDuration')}>
                    <IconHourglass size={11} /> {node.trialDuration}
                  </TrialBadge>
                )}
              </HeaderMeta>
            </NodeInfo>
          </NodeHeader>

          <DetailsScrollBody>

          <StaticSections>
          {/* Sources first — they're the source of truth. The AI text further
              down is only a summary of these, and is presented as such. */}
          {node.sources && node.sources.trim() && (
            <Section>
              <SpecialistInfo>
                <SpecialistTitle>
                  <IconBooks size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> {t('tree.sources')}
                </SpecialistTitle>
                <SourcesList
                  dangerouslySetInnerHTML={{ __html: renderSourceLinks(node.sources) }}
                />
              </SpecialistInfo>
            </Section>
          )}

          {/* Who can help — names only, no per-specialist explanation text yet */}
          {node.specialistInfo && node.specialistInfo.trim() && (
            <Section>
              <SpecialistInfo>
                <SpecialistTitle>
                  <IconStethoscope size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> {t('tree.whoCanHelp')}
                </SpecialistTitle>
                <SpecialistChips>
                  {extractSpecialistNames(node.specialistInfo).map((name, i) => (
                    <SpecialistChip key={`${name}-${i}`}>{name}</SpecialistChip>
                  ))}
                </SpecialistChips>
              </SpecialistInfo>
            </Section>
          )}

          </StaticSections>

          {/* Summary — always open, streaming in line by line. It fills the height left
              below the sources and scrolls inside its own box, so the modal doesn't. */}
          {node.description && node.description.trim() && (
            <Section $fill>
              <SpecialistInfo $fill>
                <SummaryTitle as="h4">
                  <IconSparkle size={14} />
                  {t('tree.aiSummary')}
                  {!!node.aiGenerated && (
                    <AiBadge>
                      <IconWarning size={10} /> {t('tree.aiGenerated') || 'AI-generated'}
                    </AiBadge>
                  )}
                </SummaryTitle>
                <SectionContent $scroll>
                  {descriptionBlocks.slice(0, visibleCount).map((block, i) => (
                    <FadeInBlock key={i} dangerouslySetInnerHTML={{ __html: block }} />
                  ))}
                  {visibleCount < descriptionBlocks.length && <TypingCursor />}
                </SectionContent>
              </SpecialistInfo>
            </Section>
          )}
          </DetailsScrollBody>
        </DetailsPanel>
      </PanelFrame>
    </Overlay>
  );
};

export default NodeDetails;
